import { useRef } from "react";
import type { PttSidecarConfig } from "../../pttSidecarClient";
import type { PttSidecarStatus } from "../../usePttSidecar";
import { useAwsStreamTransport } from "./useAwsStreamTransport";
import { useBlobSidecarTransport } from "./useBlobSidecarTransport";
import { useBrowserSttTransport } from "./useBrowserSttTransport";
import { usePttKeyBinding } from "./usePttKeyBinding";
import type { PttTransport } from "./types";
import { lockPttTransportKind, resolvePttTransportKind } from "./types";
import type { PttTurnSession } from "./usePttTurns";

/** Pick STT transport from sidecar config and bind the PTT key once. */
export function usePttTransport(
  config: PttSidecarConfig,
  session: PttTurnSession,
  transcribe: (blob: Blob) => Promise<string>,
  sidecarStatus: PttSidecarStatus,
): PttTransport {
  const sidecarOnline = sidecarStatus === "online";
  const resolvedKind = resolvePttTransportKind(config, sidecarOnline);
  const sessionActive = session.pttState !== "idle" || session.busyRef.current;
  const kindRef = useRef(resolvedKind);
  kindRef.current = lockPttTransportKind(resolvedKind, kindRef.current, sessionActive);
  const kind = kindRef.current;

  const aws = useAwsStreamTransport(kind === "aws" ? config : null, session);
  const blob = useBlobSidecarTransport(kind === "blob" ? config : null, session, transcribe);
  const browser = useBrowserSttTransport(kind === "browser", session);

  const transport = kind === "aws" ? aws : kind === "blob" ? blob : browser;

  usePttKeyBinding(
    {
      start: () => void transport.startRecording(),
      stop: () => transport.stopRecording(),
    },
    true,
  );

  return transport;
}
